/**
 * Route guards.
 *
 * These are a UX layer: they decide what to render, never what is permitted.
 * Every protected API call is independently gated server-side by
 * `requireMember` / `requireAdmin`, so bypassing these in devtools reveals an
 * empty shell whose data requests all return 403.
 */

import { Suspense, useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { Card } from '@heroui/react';
import { useAuth } from '../auth/AuthProvider.tsx';
import { AppShell } from './AppShell.tsx';
import { ActionButton } from './ActionButton.tsx';
import { PageLoader } from './PageLoader.tsx';
import { ChunkErrorBoundary } from './ChunkErrorBoundary.tsx';
import { useAction } from '../lib/useAction.ts';
import { notify } from '../lib/notify.tsx';

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="grid min-h-dvh place-items-center bg-background px-4 py-6 text-foreground">
      {children}
    </div>
  );
}

export function RequireMember() {
  const { status, message, refresh } = useAuth();
  // Before every early return: a component may not call fewer hooks on the
  // renders where it bails out.
  const action = useAction();

  if (status === 'loading') {
    // Full height rather than the content area's: there is no shell yet, so
    // this is the whole screen.
    return (
      <div className="grid min-h-dvh place-items-center bg-background text-foreground">
        <PageLoader label="Проверяем ваше членство в группе…" />
      </div>
    );
  }

  if (status === 'not_a_member') return <Navigate to="/not-a-member" replace />;
  if (status === 'unauthenticated') return <Navigate to="/login" replace />;

  if (status === 'stale_launch_data') {
    // No retry button, deliberately. The cached launch data carries its
    // original auth_date and only Telegram can mint a newer one, so a retry
    // would fail identically every time and teach the member to distrust it.
    //
    // And no claim about membership. `/auth/miniapp` rejects a stale payload
    // BEFORE `establishSession`, so `getChatMember` never ran — the server
    // does not know whether this person is still in the group, and someone who
    // was removed while the app sat open would be reading a reassurance that
    // was never checked. Say only what is known: the launch data is old.
    return (
      <Centered>
        <Card className="w-full max-w-md">
          <Card.Header>
            <Card.Title>Данные запуска устарели</Card.Title>
          </Card.Header>
          <Card.Content>
            <p className="text-sm text-muted">
              Приложение было открыто слишком давно. Закройте его и откройте из Telegram заново.
            </p>
          </Card.Content>
        </Card>
      </Centered>
    );
  }

  if (status === 'unavailable') {
    // Deliberately NOT phrased as a rejection: we could not ask Telegram, which
    // is a different thing from Telegram saying no.
    return (
      <Centered>
        <Card className="w-full max-w-md">
          <Card.Header>
            <Card.Title>Сейчас не удаётся проверить</Card.Title>
          </Card.Header>
          <Card.Content className="flex flex-col items-start gap-4">
            <p className="text-sm text-muted">
              {message ?? 'Не удалось связаться с Telegram, чтобы подтвердить членство в группе.'}
            </p>
            {/*
              ⚠️ THE OUTCOME IS THE PAGE ITSELF, WHICH IS WHY THERE IS NO
              MESSAGE HERE. `refresh` re-derives the session and never throws —
              a success replaces this screen with the app and a failure repaints
              this card with whatever Telegram said this time. The runner is
              here for the WAIT: `getChatMember` is a round trip to Telegram,
              and without a spinner the button reads as dead for a second.
            */}
            <ActionButton
              isPending={action.isPending('retry')}
              isDisabled={action.isBusy}
              onPress={() =>
                void action.run('retry', refresh, {
                  error: 'Не удалось связаться с Telegram.',
                })
              }
            >
              Попробовать снова
            </ActionButton>
          </Card.Content>
        </Card>
      </Centered>
    );
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

/**
 * The last admin route a denial was announced for, and when.
 *
 * ⚠️ MODULE-LEVEL BECAUSE A REF WOULD NOT SURVIVE THE REMOUNT IT EXISTS TO
 * ABSORB. `<StrictMode>` mounts, runs effects, unmounts and mounts again on
 * every development build — so a plain `useEffect` fires the toast twice, and a
 * `useRef` guard is reset by the very remount it was meant to catch. Production
 * strips that behaviour, but the person who sees this first is running `bun run
 * dev`, and a doubled toast reads as a bug in the toast.
 *
 * The window is short on purpose: a second attempt a minute later is a real
 * second denial and deserves to be told again.
 */
let lastDenial: { path: string; at: number } | null = null;
const DENIAL_REPEAT_MS = 1_000;

/**
 * Admin-only subtree. Compose INSIDE RequireMember.
 *
 * ⚠️ THE REDIRECT USED TO BE SILENT, and silence is the wrong answer to the one
 * case that actually happens. A member cannot see the Админка tab — `AppShell`
 * filters it — so the way here is an old bookmark, a pasted link, or being
 * DEMOTED with the tab still open: the nav item disappears on the next
 * `/api/me`, and until then pressing it lands you on Главная with no account of
 * why. That reads as the app losing the click.
 *
 * ⚠️ IT IS THE SERVER'S OWN SENTENCE, WORD FOR WORD (`middleware/auth.ts`).
 * Reaching an admin page and calling an admin endpoint are the same refusal for
 * the same reason, and hearing it phrased two ways would suggest they are two
 * different rules.
 *
 * TODO(OPEN-ITEM-1): which pages live behind this is unspecified.
 * See docs/OPEN_ITEMS.md.
 */
export function RequireAdmin() {
  const { isAdmin, status } = useAuth();
  const { pathname } = useLocation();

  /*
    ⚠️ AN EFFECT, NOT A CALL IN THE RENDER BODY, and ABOVE the early returns.
    Queueing a toast is a side effect — React may render this component without
    committing it — and a component may not call fewer hooks on the renders
    where it bails out.

    Only the `isAdmin` branch announces. `status !== 'authenticated'` is a
    signed-out visitor being sent to the login page, which is not a statement
    about their role and already explains itself by arriving at a login form.
  */
  const denied = status === 'authenticated' && !isAdmin;
  useEffect(() => {
    if (!denied) return;
    const now = Date.now();
    if (lastDenial && lastDenial.path === pathname && now - lastDenial.at < DENIAL_REPEAT_MS) {
      return;
    }
    lastDenial = { path: pathname, at: now };
    notify.info('Этот раздел доступен только администраторам группы.');
  }, [denied, pathname]);

  if (status !== 'authenticated') return <Navigate to="/login" replace />;
  if (denied) return <Navigate to="/" replace />;

  /*
    ⚠️ THE ONE <Suspense> FOR THE WHOLE ADMIN SUBTREE. Every admin page is a
    `lazy()` import (see main.tsx), which is what keeps the chart library and
    the admin tables out of the bundle a member downloads on their phone. One
    boundary here covers all eight pages, because this component already owns
    their shared <Outlet/>.

    The fallback is `PageLoader`, which waits 260ms before showing itself — the
    chunk is served from the same origin as the page that just rendered, so on
    every navigation after the first there is nothing to see and the reader gets
    no flash at all.
  */
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={<PageLoader />}>
        <Outlet />
      </Suspense>
    </ChunkErrorBoundary>
  );
}
